import { ExternalLinkIcon } from "@chakra-ui/icons";
import {
  Text,
  Link,
  VStack,
  Button,
  Stack,
  useBreakpointValue,
  Tooltip,
} from "@chakra-ui/react";
import { parseDate, parseOptional } from "@syla/shared/types/helpers/parsing";
import {
  WalletImportMethod,
  SyncState,
} from "@syla/shared/types/models/WalletBase";
import React from "react";
import { AiOutlineSync } from "react-icons/ai";
import {
  PageContainer,
  PageHeadingContainer,
  StdVStack,
} from "../components/atoms/Containers";
import { PageHeading } from "../components/atoms/Headings";
import { BalancesDatePicker } from "../components/organisms/balances/BalancesDatePicker";
import {
  BalancesTable,
  ViewBy,
} from "../components/organisms/balances/BalancesTable";
import {
  ButtonGroupItem,
  SingleSelectButtonGroup,
} from "../components/organisms/dataSource/dataSourceSelector/SingleSelectButtonGroup";
import { useLocalStorage } from "../hooks/localStorage/useLocalStorage";
import { ReactComponent as AssetIcon } from "../images/icons/asset.svg";
import { ReactComponent as WalletIcon } from "../images/icons/wallet.svg";
import { reSyncWallets } from "../store/actions/reSyncWallets";
import { useGetWallets } from "../store/actions/wallet";
import { useCurrentAccountStore } from "../store/currentAccountStore";

export function Balances() {
  const accountId = useCurrentAccountStore(({ accountId }) => accountId);

  const [date, setDate] = useLocalStorage<string | undefined>(
    "balancesDate",
    undefined,
  );
  const [viewBy, setViewBy] = useLocalStorage<ViewBy>(
    "balancesViewBy",
    "dataSource",
  );

  const { data: walletHoldingsResponse } = useGetWallets(accountId);

  const smallScreen = useBreakpointValue({
    base: true,
    "2xs": true,
    xs: true,
    sm: true,
    md: false,
  });

  const viewByOptions: ButtonGroupItem<ViewBy>[] = [
    {
      value: "dataSource",
      label: "Data Source",
      buttonProps: {
        leftIcon: <WalletIcon />,
      },
    },
    {
      value: "asset",
      label: "Asset",
      buttonProps: {
        leftIcon: <AssetIcon />,
      },
    },
  ];

  return (
    <PageContainer>
      <PageHeadingContainer>
        <VStack align="flex-start">
          <PageHeading>Balances</PageHeading>
          <Text color="gray.800">
            Analyse your current and historical balances across all data
            sources.
          </Text>
          <Link
            href="https://help.syla.com.au/en/articles/9488227-balances-guide"
            fontWeight="bold"
            color="primary.500"
            isExternal
          >
            Guide to balances <ExternalLinkIcon />
          </Link>
        </VStack>
        <Tooltip
          shouldWrapChildren
          label="Sync the latest transaction data from all eligible data sources"
        >
          <Button
            backgroundColor="white"
            variant="outline"
            colorScheme="gray"
            leftIcon={<AiOutlineSync />}
            isDisabled={!walletHoldingsResponse}
            isLoading={walletHoldingsResponse?.wallets.some(
              (wallet) => wallet.syncState === SyncState.InProgress,
            )}
            onClick={(event) => {
              event.stopPropagation();
              reSyncWallets(
                accountId,
                walletHoldingsResponse!.wallets.filter(
                  (wallet) => wallet.importMethod === WalletImportMethod.API,
                ),
              );
            }}
          >
            Sync All
          </Button>
        </Tooltip>
      </PageHeadingContainer>

      <StdVStack spacing={6}>
        <Stack
          direction={smallScreen ? "column" : "row"}
          gridGap={smallScreen ? 5 : 20}
        >
          <BalancesDatePicker
            date={parseOptional(date, parseDate)}
            onChange={(value) => {
              setDate(value?.toISOString());
            }}
          />
          <VStack alignItems="flex-start">
            <Text>View by</Text>
            <SingleSelectButtonGroup
              backgroundColor="white"
              isAttached
              variant="outline"
              colorScheme="gray"
              spacing={0}
              options={viewByOptions}
              value={viewBy as ViewBy}
              onChange={(value) => setViewBy(value as ViewBy)}
            />
          </VStack>
        </Stack>
        <BalancesTable
          balanceDate={date ? parseDate(date) : undefined}
          viewBy={viewBy!}
          smallScreen={smallScreen}
        />
      </StdVStack>
    </PageContainer>
  );
}
